import React from "react";
import { CircularProgress } from "@mui/material";
import { ContextMeshPlaceholder, StrengthWheel } from "../meshStrength/StrengthWheel";
import type { ContextMeshCategoryScores, ContextMeshTier } from "../meshStrength/contextMeshAnalysis";
import { CONTEXT_MESH_CATEGORIES } from "../meshStrength/contextMeshTheme";
import { draftContextMeshStageMessage, type DraftContextMeshAnalysisStage, type DraftContextMeshStatus } from "./draftContextMeshApi";

export interface CanonicalMeshCardAnalysis {
    tier: ContextMeshTier;
    score: number;
    readinessScore: number;
    qualityScore: number;
    categoryScores: ContextMeshCategoryScores;
}
export type CanonicalMeshCardStatus = DraftContextMeshStatus | "debouncing" | "unavailable" | "stale" | "analyzing";

function emptyMessage(status: CanonicalMeshCardStatus, hasError: boolean): string {
    if (hasError || status === "failed" || status === "unavailable") return "Context analysis unavailable";
    if (status === "baseline_incomplete") return "Complete the required fields to begin";
    if (status === "sources_pending") return "Waiting for source processing…";
    if (status === "debouncing") return "Analysis begins after you pause…";
    return "Analyzing context…";
}

export function MeshCard({ analysis, status, updating, stale, error, open, onToggle, onRetry, analysisStage, analysisMessage }: {
    analysis: CanonicalMeshCardAnalysis | null;
    status: CanonicalMeshCardStatus;
    updating: boolean;
    stale: boolean;
    error: string | null;
    analysisStage?: DraftContextMeshAnalysisStage;
    analysisMessage?: string;
    open: boolean;
    onToggle: () => void;
    onRetry: () => void;
}) {
    const canReview = analysis !== null;
    const progressMessage = updating && status === "sources_pending"
        ? draftContextMeshStageMessage("waiting_sources")
        : updating && ["pending", "analyzing"].includes(status)
          ? analysisMessage ?? draftContextMeshStageMessage(analysisStage) : undefined;
    return (
        <div
            onClick={() => canReview && onToggle()}
            data-testid="new-product-context-mesh-card"
            style={{
                background: "var(--bg-paper)", border: open ? "1.5px solid var(--np-brand)" : "1px solid var(--divider)",
                borderRadius: open ? "0 18px 18px 0" : 18, boxShadow: "var(--shadow-1)", padding: "16px 18px",
                display: "flex", flexDirection: "column", alignItems: "center", cursor: canReview ? "pointer" : "default",
                transition: "border-radius .25s, border-color .25s", height: "100%"
            }}
        >
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--fg4)", marginBottom: 6 }}>
                Context mesh
            </div>
            <div style={{ width: 160, height: 160 }}>
                {analysis ? (
                    <StrengthWheel score={analysis.score} tier={analysis.tier} categoryScores={analysis.categoryScores} uid="new-product" showHub={false} showLabels={false} />
                ) : (
                    <ContextMeshPlaceholder showLabel={false} />
                )}
            </div>
            {analysis ? (
                <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 7, marginTop: 8 }}>
                    {CONTEXT_MESH_CATEGORIES.map(quadrant => {
                        const category = analysis.categoryScores[quadrant.key];
                        return (
                            <div key={quadrant.key}>
                                <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 3 }}>
                                    <span style={{ fontSize: 10.5, fontWeight: 650, color: "var(--fg2)" }}>{quadrant.title}</span>
                                    <span style={{ flex: 1 }} />
                                    <span style={{ fontSize: 10, fontWeight: 700, color: quadrant.accentColor }}>{category.score}%</span>
                                </div>
                                <div style={{ height: 5, borderRadius: 5, background: "var(--bg-input)", overflow: "hidden" }}>
                                    <div style={{ width: `${category.score}%`, height: "100%", borderRadius: 5, background: quadrant.color, transition: "width .3s ease" }} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div style={{ minHeight: 105, display: "grid", placeItems: "center", textAlign: "center", fontSize: 11.5, color: "var(--fg3)", lineHeight: 1.45 }}>
                    {error ?? progressMessage ?? emptyMessage(status, false)}
                </div>
            )}
            <div aria-live="polite" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 10, minHeight: 27, padding: "5px 12px", borderRadius: 999, border: `1px solid ${error ? "var(--np-warn)" : "var(--np-brand)"}`, color: error ? "var(--np-warn)" : "var(--np-brand)", fontSize: 11, fontWeight: 700 }}>
                {updating && <CircularProgress size={11} sx={{ color: "currentColor" }} />}
                {error ? (
                    <button type="button" onClick={event => { event.stopPropagation(); onRetry(); }} style={{ all: "unset", cursor: "pointer" }}>
                        Update failed — Retry
                    </button>
                ) : analysis ? (
                    updating
                        ? progressMessage ?? "Updating context score…"
                        : status === "baseline_incomplete"
                          ? "Complete required fields to update"
                          : stale
                            ? "Previous context score"
                            : "Review Context Mesh"
                ) : updating ? (
                    analysisStage === "queued_for_capacity" && status !== "sources_pending" ? "Queued…" : "Analyzing…"
                ) : (
                    "Context score pending"
                )}
            </div>
        </div>
    );
}
